// SF2 — Orders by Fulfilment Channel.
//
// Order counts here are fully additive: an order carries exactly one channel,
// so the columns simply sum. None of the primary-category language from the
// category table applies, and it is deliberately not reused.
//
// The two groups answer different questions. Single-channel products are the
// normal case. Multi-channel products are the ones worth a look, and they are
// ordered by revenue OUTSIDE their biggest channel rather than by total, so the
// list surfaces genuinely split products instead of simply the biggest sellers.
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiCheckCircle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../OrderAnalytics.module.css';
import { getChannels } from '../services/orderAnalyticsApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const Blank = () => <span className={local.blank}>—</span>;

const money = (value) =>
  value === null || value === undefined ? <Blank /> : `₹${inr.format(Math.round(value))}`;

const count = (value) =>
  value === null || value === undefined ? <Blank /> : inr.format(value);

const Change = ({ value }) => {
  if (value === null || value === undefined) return <Blank />;
  const up = value > 0;
  return (
    <span className={up ? local.changeUp : local.changeDown}>
      {up ? '+' : ''}{(value * 100).toFixed(1)}%
    </span>
  );
};

const ALL = '__all__';

const ChannelBreakdown = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [channelFilter, setChannelFilter] = useState(ALL);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getChannels(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);
  useEffect(() => { setChannelFilter(ALL); }, [data]);

  const mix = data?.mix || [];
  const single = useMemo(() => {
    const rows = data?.single_channel || [];
    return channelFilter === ALL ? rows
      : rows.filter((row) => row.channel === channelFilter);
  }, [data, channelFilter]);
  const multi = data?.multi_channel || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Orders by fulfilment channel</h2>
        {data && (
          <span className={local.muted}>
            {count(mix.reduce((total, row) => total + row.orders, 0))} orders
          </span>
        )}
      </div>
      <p className={local.muted}>
        Every order carries one channel, so these counts add up exactly.
      </p>

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the channel mix'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>Channel</th>
                  <th className={local.numeric}>Revenue</th>
                  <th className={local.numeric}>Units</th>
                  <th className={local.numeric}>Orders</th>
                  <th className={local.numeric}>Share</th>
                  <th className={local.numeric}>Revenue trend</th>
                  <th className={local.numeric}>Units trend</th>
                </tr>
              </thead>
              <tbody>
                {mix.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={7}>
                      {loading ? 'Loading…' : 'No orders in this period.'}
                    </td>
                  </tr>
                ) : mix.map((row) => (
                  <tr key={row.channel}>
                    <td className={row.unmapped ? local.uncategorised : undefined}>
                      {row.channel}
                    </td>
                    <td className={local.numeric}>{money(row.revenue)}</td>
                    <td className={local.numeric}>{count(row.units)}</td>
                    <td className={local.numeric}>{count(row.orders)}</td>
                    <td className={`${local.numeric} ${local.shareCell}`}>
                      <span className={local.shareBar}
                        style={{ width: `${(row.share_of_revenue || 0) * 100}%` }} />
                      <span className={local.shareValue}>
                        {row.share_of_revenue === null || row.share_of_revenue === undefined
                          ? <Blank /> : `${(row.share_of_revenue * 100).toFixed(1)}%`}
                      </span>
                    </td>
                    <td className={local.numeric}><Change value={row.revenue_change} /></td>
                    <td className={local.numeric}><Change value={row.units_change} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Group B first: an empty one is the normal case and should read as
              nothing needing attention, not as a broken panel. */}
          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              Selling on more than one channel
              <span className={local.groupCount}>{count(data?.multi_channel_count)}</span>
            </h3>
          </div>
          {multi.length === 0 ? (
            <div className={local.allClear}>
              <FiCheckCircle />
              No product sold through more than one channel in this period.
            </div>
          ) : (
            <div className={local.tableScroll}>
              <table className={local.table}>
                <thead>
                  <tr>
                    <th>ASIN</th>
                    <th>Split</th>
                    <th className={local.numeric}>Revenue</th>
                    <th className={local.numeric}>Outside main channel</th>
                  </tr>
                </thead>
                <tbody>
                  {multi.map((row) => (
                    <tr key={row.asin}>
                      <td className={local.asinCell}>{row.asin}</td>
                      <td>
                        {row.channels.map((entry) => (
                          <span key={entry.channel} className={local.channelChip}>
                            {entry.channel} {money(entry.revenue)}
                          </span>
                        ))}
                      </td>
                      <td className={local.numeric}>{money(row.revenue)}</td>
                      <td className={local.numeric}>
                        {money(row.non_dominant_revenue)}
                        {row.non_dominant_share !== null && (
                          <span className={local.muted}>
                            {' '}({(row.non_dominant_share * 100).toFixed(0)}%)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className={local.groupHead}>
            <h3 className={local.groupTitle}>
              Selling on one channel
              <span className={local.groupCount}>{count(data?.single_channel_count)}</span>
            </h3>
            <div className={local.segmented}>
              <button type="button"
                className={channelFilter === ALL ? local.segmentOn : local.segment}
                onClick={() => setChannelFilter(ALL)}>All</button>
              {mix.map((row) => (
                <button key={row.channel} type="button"
                  className={channelFilter === row.channel ? local.segmentOn : local.segment}
                  onClick={() => setChannelFilter(row.channel)}>{row.channel}</button>
              ))}
            </div>
          </div>
          <div className={local.tableScroll}>
            <table className={local.table}>
              <thead>
                <tr>
                  <th>ASIN</th>
                  <th>Channel</th>
                  <th className={local.numeric}>Revenue</th>
                  <th className={local.numeric}>Units</th>
                  <th className={local.numeric}>Orders</th>
                </tr>
              </thead>
              <tbody>
                {single.length === 0 ? (
                  <tr>
                    <td className={local.emptyCell} colSpan={5}>
                      {loading ? 'Loading…' : 'No products on this channel in this period.'}
                    </td>
                  </tr>
                ) : single.slice(0, 50).map((row) => (
                  <tr key={row.asin}>
                    <td className={local.asinCell}>{row.asin}</td>
                    <td>{row.channel}</td>
                    <td className={local.numeric}>{money(row.revenue)}</td>
                    <td className={local.numeric}>{count(row.units)}</td>
                    <td className={local.numeric}>{count(row.orders)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {single.length > 50 && (
            <p className={local.muted}>Showing the 50 highest earning of {count(single.length)}.</p>
          )}
        </>
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default ChannelBreakdown;
